import type { Auth } from 'firebase/auth';

/**
 * Plugin that automatically injects Firebase ID token into all /api/admin requests.
 * Works with both useFetch and $fetch - no changes needed at call sites.
 * Only initializes Firebase when making admin API requests.
 */
export default defineNuxtPlugin(() => {
	let auth: Auth | null = null;

	async function getFirebaseAuth(): Promise<Auth | null> {
		if (auth) return auth;

		// Dynamic import - only loads Firebase when needed
		const { getApps, getApp } = await import('firebase/app');
		const { getAuth } = await import('firebase/auth');

		// Only get auth if Firebase is already initialized by the composable
		const apps = getApps();
		if (apps.length === 0) {
			return null;
		}

		auth = getAuth(getApp());
		return auth;
	}

	globalThis.$fetch = $fetch.create({
		async onRequest({ options, request }) {
			// Only add auth header to admin API routes
			const url = typeof request === 'string' ? request : request.toString();
			if (!url.includes('/api/admin')) {
				return;
			}

			const firebaseAuth = await getFirebaseAuth();
			const user = firebaseAuth?.currentUser;
			if (!user) {
				return;
			}

			const token = await user.getIdToken();
			if (token) {
				const headers = new Headers(options.headers as HeadersInit);
				headers.set('Authorization', `Bearer ${token}`);
				options.headers = headers;
			}
		},
		async onResponseError({ response }) {
			// On 401, try to refresh the token and the caller can retry
			if (response.status === 401) {
				const firebaseAuth = await getFirebaseAuth();
				const user = firebaseAuth?.currentUser;
				if (user) {
					// Force refresh the token for next request
					await user.getIdToken(true);
				}
			}
		},
	});
});
